# Questionnaires

Questionnaires allow hirers to present a set of questions to candidates when they complete SEEK’s Apply Form.
[SEEK Optimised Apply] enables the best candidate experience and conversion rates.
Your software can ingest applications from SEEK,
including [questionnaire submissions] and [scoring].

import { Questionnaire } from './Questionnaire';

<Questionnaire />

You may have opted out of questionnaire support with your initial SEEK API integration due to time constraints or because the implementation options available at the time were not suitable for your software.
For example, the SEEK API first launched with [ATS Screening Questions] in 2020,
but it wouldn’t have made sense to implement that option if your software did not have its own question library and questionnaire management capabilities.
We have since developed new options like a SEEK-built Questionnaire Panel,
so we encourage you to review what is available and factor questionnaire enhancements in to your product roadmap.

The following table outlines our recommended next steps based on whether you have implemented a SEEK questionnaire option in your software.

import { IconClear, IconTick } from 'braid-design-system';

|     Questionnaire Panel?     |   ATS Screening Questions?   | Recommendation                                                                   |
| :--------------------------: | :--------------------------: | :------------------------------------------------------------------------------- |
| <IconTick tone="positive" /> | <IconClear tone="neutral" /> | Complete [Uplifting the Questionnaire Panel](#uplifting-the-questionnaire-panel) |
| <IconClear tone="neutral" /> | <IconTick tone="positive" /> | [Skip to the next page]                                                          |
| <IconClear tone="neutral" /> | <IconClear tone="neutral" /> | Review [Implementing SEEK questionnaires](#implementing-seek-questionnaires)     |

[questionnaire submissions]: /use-cases/application-export/questionnaire-submissions
[scoring]: /use-cases/application-export/questionnaire-submissions#preferred-choices--scoring
[SEEK Optimised Apply]: /use-cases/application-export

## Implementing SEEK questionnaires

The implementation option we recommend largely depends on whether your software has its own question library and questionnaire management capabilities:

```mermaid Questionnaire options
flowchart TD
  start{Do you have a<br/> question library?} -->|Yes| option1(ATS Screening Questions)
  start -->|No| option2("Questionnaire Panel")
```

Keep in mind that it will be intrinsically simpler for you to build advanced screening functionality when you control the question library.
If your software supports applicant filtering and ranking based on questionnaire submissions,
you may wish to consider [ATS Screening Questions](#option-2-ats-screening-questions) or [linking out] to an external apply form.

[linking out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form

### Option 1: Questionnaire Panel

import { Alert, Text } from 'braid-design-system';

<Alert tone="promote">
  <Text>
    SEEK strongly recommends using the Questionnaire Panel for your integration.
    It will significantly reduce development time and ongoing maintenance, as
    well as provide an up-to-date user experience for SEEK hirers.
  </Text>
</Alert>

We now offer a Questionnaire Panel that you can embed into your job posting workflow.
Hirers can interact with the panel to author questionnaires using SEEK’s question library and recommendations.

The Questionnaire Panel provides access to SEEK’s curated library of over 25,000 questions.
Hirers may choose up to 8 library questions from a list of recommendations that are tailored to the position’s location, job category and title,
and create up to 3 custom questions to capture niche or open-ended responses.
SEEK questionnaires currently support free text, single select and multi select questions,
and can be configured to require the acceptance of a privacy policy.

You can preview a screenshot of the Questionnaire Panel below,
and jump to the [implementation guide](/use-cases/job-posting/questionnaires/v2/panel) to get started.

![Questionnaire Panel screenshot](./QuestionnairePanel.png '=892x833 Questionnaire Panel screenshot')

### Option 2: ATS Screening Questions

If your software already has its own question library and questionnaire management capabilities,
you can implement ATS Screening Questions.

This option allows hirers to reuse the question capabilities in your software that they are already familiar with.
Your software can then map the selected questions across to the SEEK API so they can be attached to job ads.
SEEK questionnaires currently support free text, single select and multi select questions,
as well as the acceptance of a privacy policy.
You can nominate preferred responses for selection questions to drive [scoring] of candidate question submissions.

Jump to the [implementation guide](/use-cases/job-posting/questionnaires/ats-questions) to get started.

## Uplifting the Questionnaire Panel

We have incorporated the Questionnaire Panel into our unified [software development kit] alongside the [new Ad Selection Panel].
The new SDK makes it simpler for your software to embed multiple panels,
and enables SEEK to safely enhance the panel over time with built-in support for beta testing and staged rollouts.

[software development kit]: https://en.wikipedia.org/wiki/Software_development_kit

### Render the panel

Adopt this change by including the new script tag on the webpage where the panel will be displayed:

```diff label="HTML"
<script
  type="text/javascript"
- src="https://seekcdn.com/hirer/indirect-posting/questionnaire-panel/SEEKQuestionnairePanel.js"
+ src="https://integration.seek.com/panels/SeekApi.js"
></script>
```

While its visual refresh will be noticeable,
the new Questionnaire Panel also integrates with your software differently under the hood.
Render via `SeekApi.render`:

```diff label="JavaScript"
- SEEKQuestionnairePanel.render(containerNode, options);
+ const { dispatchEvent } = SeekApi.render(containerNode, 'questionnaire', props);
```

The options supported are largely identical,
but we have removed `errorHandler` as the SDK will display consistent and actionable error messages to the end user.

The SEEK API will now tailor question recommendations to the [location of the job ad] and domicile of the [SEEK hirer account].
This enhancement works transparently without any additional partner effort.

### Support Job Posting

The new `dispatchEvent('seek:questionnaire:save')` function call replaces the previous `SEEKQuestionnairePanel.save()` method.

```javascript label="Before"
const postSeekJobAd = async (input) => {
  // Call a posting mutation to post or update a SEEK job ad.
};

const handleSubmitButton = async () => {
  const input = {
    // Compose from form data.
  };

  const result = await SEEKQuestionnairePanel.save();

  if (result.errorCode) {
    // Do not post the job ad if an error occurs while saving the questionnaire.
    // The panel will display an error message to the hirer.
    return;
  }

  // Data will be nullish if the hirer did not add any questions via the panel.
  if (result.data?.id) {
    input.positionProfile.seekApplicationQuestionnaireId = result.data.id;
  }

  await postSeekJobAd(input);
};
```

```javascript label="After"
const postSeekJobAd = async (input) => {
  // Call a posting mutation to post or update a SEEK job ad.
};

const handleSubmitButton = async () => {
  const input = {
    // Compose from form data.
  };

  const result = await dispatchEvent('seek:questionnaire:save');

  if (result.isError) {
    // Do not post the job ad if an error occurs while saving the questionnaire.
    // The panel will display an actionable error message to the hirer.
    return;
  }

  // Data will be nullish if the hirer did not add any questions via the panel.
  if (result.data?.id) {
    input.positionProfile.seekApplicationQuestionnaireId = result.data.id;
  }

  await postSeekJobAd(input);
};
```

The `result` from saving the questionnaire has been slightly tweaked,
but it should be functionally equivalent:

| Before      | After     | Description              |
| :---------- | :-------- | ------------------------ |
| `data`      | `data`    | Same behaviour as before |
| `data.id`   | `data.id` | Same behaviour as before |
| `errorCode` | `isError` | Simplified to a boolean  |

Job ad drafts can continue to be supported in the same manner,
with your software invoking `dispatchEvent('seek:questionnaire:save')` instead of `SEEKQuestionnairePanel.save()`.

[ATS Screening Questions]: /use-cases/job-posting/questionnaires/ats-questions
[location of the job ad]: /schema/named-type/ApplicationLibraryQuestionSuggestions_PositionProfileInput#/field/positionLocation
[new Ad Selection Panel]: /migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/ad-selection
[SEEK hirer account]: /schema/named-type/ApplicationLibraryQuestionSuggestions_PositionProfileInput#/field/positionOrganizations
[skip to the next page]: /migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/job-ad-preview
